import { Link } from "react-router-dom";
import { Phone, Mail, Building2, KeyRound, BedDouble, Landmark, Zap, FileText, Handshake, RefreshCcw, QrCode, Download, CalendarCheck, ArrowRight } from "lucide-react";
import { Reveal, MaskedLine, SectionTag } from "@/components/Reveal";
import CtaSection from "@/components/CtaSection";
import Seo from "@/components/Seo";
import { CALENDLY_URL } from "@/data/site";

const AUDIENCES = [
    { icon: Landmark, title: "Syndics & bailleurs", text: "Parties communes, logements infestés, coordination avec les locataires : nous gérons l'intervention de A à Z et vous tenons informé à chaque étape." },
    { icon: KeyRound, title: "Agences immobilières", text: "Avant une vente, une location ou un état des lieux : un logement certifié traité rassure vos clients et sécurise vos transactions." },
    { icon: BedDouble, title: "Conciergeries & location saisonnière", text: "Le turnover de voyageurs multiplie le risque de punaises de lit. Intervention express entre deux séjours pour ne jamais bloquer une réservation." },
    { icon: Building2, title: "Hôtels, gîtes & campings", text: "Une chambre bloquée coûte plus cher qu'un traitement. Protocole discret et rapide, avec rapport écrit à l'appui.", href: "/hotels" },
];

const ARGUMENTS = [
    { icon: Zap, title: "Réactivité 2h, 7j/7", text: "Un départ du Mont-Dol en moins de 2h, week-ends et jours fériés inclus. Vos logements ne restent jamais bloqués." },
    { icon: FileText, title: "Rapport d'intervention", text: "Après chaque passage, vous recevez un compte rendu écrit : traçabilité complète pour vos dossiers et vos clients." },
    { icon: RefreshCcw, title: "Contrats annuels", text: "Des contrats de protection permanente, particuliers comme professionnels, adaptés au volume d'interventions." },
    { icon: Handshake, title: "Interlocuteur unique", text: "Vous échangez directement avec le technicien certifié CertiBIOCIDE qui intervient. Pas de plateforme, pas de sous-traitance." },
];

const Pros = () => (
    <div data-testid="pros-page">
        <Seo
            title="Espace professionnels : syndics, agences, conciergeries | Eradikbreizh"
            description="Eradikbreizh accompagne syndics, bailleurs, agences immobilières, conciergeries et hébergements touristiques contre punaises de lit et cafards en Bretagne : intervention sous 2h, 7j/7, rapport écrit, contrats annuels."
        />

        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-b from-mist to-white pb-20 pt-40 md:pt-48">
            <div className="absolute -right-40 top-0 h-[30rem] w-[30rem] rounded-full bg-brand/10 blur-[160px]" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                <MaskedLine delay={0.1}>
                    <span className="inline-flex items-center gap-3 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600 shadow-sm">
                        <Building2 className="h-4 w-4 text-brand" />
                        Espace professionnels
                    </span>
                </MaskedLine>
                <MaskedLine delay={0.25}>
                    <h1 className="mt-8 max-w-4xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-6xl">
                        Votre partenaire anti-nuisibles <span className="text-brand">pour vos logements</span>
                    </h1>
                </MaskedLine>
                <MaskedLine delay={0.4}>
                    <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                        Syndics, bailleurs, agences immobilières, conciergeries et hébergements touristiques : Eradikbreizh traite les punaises de lit et les cafards dans vos biens, partout dans un rayon de 80 km autour du Mont-Dol.
                    </p>
                </MaskedLine>
                <MaskedLine delay={0.55}>
                    <div className="mt-10 flex flex-wrap gap-4">
                        <a
                            data-testid="pros-phone-button"
                            href="tel:+33255992243"
                            className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95"
                        >
                            <Phone className="h-5 w-5" />
                            02 55 99 22 43
                        </a>
                        <a
                            data-testid="pros-email-button"
                            href="mailto:contact@eradikbreizh.fr"
                            className="inline-flex items-center gap-2 rounded-full border border-black/15 px-8 py-4 font-bold text-night transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-black/5"
                        >
                            <Mail className="h-5 w-5 text-brand" />
                            contact@eradikbreizh.fr
                        </a>
                    </div>
                </MaskedLine>
            </div>
        </section>

        {/* AUDIENCES */}
        <section className="bg-white py-20 md:py-24">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal><SectionTag>Pour qui ?</SectionTag></Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        Des solutions pour <span className="text-brand">chaque acteur du logement</span>
                    </h2>
                </Reveal>
                <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {AUDIENCES.map((a, i) => (
                        <Reveal key={a.title} delay={0.08 * i} className="h-full">
                            <div data-testid={`pros-audience-${i}`} className="h-full rounded-3xl border border-black/10 bg-mist p-8 transition-transform duration-300 hover:-translate-y-1">
                                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                    <a.icon className="h-6 w-6" />
                                </div>
                                <h3 className="mt-5 font-display text-lg font-black uppercase tracking-tight">{a.title}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-slate-600">{a.text}</p>
                                {a.href && (
                                    <Link
                                        data-testid={`pros-audience-${i}-link`}
                                        to={a.href}
                                        className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-brand transition-colors hover:text-brand-hover"
                                    >
                                        Découvrir l'offre hôtels
                                        <ArrowRight className="h-4 w-4" />
                                    </Link>
                                )}
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {/* ARGUMENTS */}
        <section className="bg-mist py-20 md:py-24">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal><SectionTag>Pourquoi nous ?</SectionTag></Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        Un service pensé pour <span className="text-brand">les gestionnaires</span>
                    </h2>
                </Reveal>
                <div className="mt-14 grid gap-6 sm:grid-cols-2">
                    {ARGUMENTS.map((a, i) => (
                        <Reveal key={a.title} delay={0.08 * i}>
                            <div data-testid={`pros-argument-${i}`} className="flex items-start gap-5 rounded-3xl border border-black/10 bg-white p-8 shadow-sm">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand text-white">
                                    <a.icon className="h-6 w-6" />
                                </div>
                                <div>
                                    <h3 className="font-display text-lg font-black uppercase tracking-tight">{a.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{a.text}</p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {/* QR CODE */}
        <section className="bg-white py-20 md:py-24">
            <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
                <div>
                    <Reveal><SectionTag>Outil prescripteur</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            Un QR code à <span className="text-brand">afficher partout</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 max-w-lg text-sm leading-relaxed text-slate-600 sm:text-base">
                            Imprimez ce QR code dans vos parties communes, vos welcome books ou vos documents de remise de clés : vos locataires et voyageurs réservent une intervention en 2 minutes, sans vous solliciter.
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <a
                                data-testid="pros-qr-download"
                                href="/assets/qr-espace-pro.png"
                                download="qr-eradikbreizh-espace-pro.png"
                                className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95"
                            >
                                <Download className="h-5 w-5" />
                                Télécharger le QR code
                            </a>
                            <a
                                data-testid="pros-booking-button"
                                href={CALENDLY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-full border border-black/15 px-7 py-3.5 font-bold text-night transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-black/5"
                            >
                                <CalendarCheck className="h-5 w-5 text-brand" />
                                Planifier un premier échange
                            </a>
                        </div>
                    </Reveal>
                </div>
                <Reveal delay={0.25}>
                    <div className="mx-auto max-w-sm rounded-3xl border border-black/10 bg-white p-8 text-center shadow-[0_20px_60px_rgba(10,15,36,0.12)]">
                        <QrCode className="mx-auto h-6 w-6 text-brand" />
                        <img
                            data-testid="pros-qr-image"
                            src="/assets/qr-espace-pro.png"
                            alt="QR code vers l'espace professionnels Eradikbreizh"
                            className="mx-auto mt-4 w-full rounded-xl"
                        />
                        <p className="mt-4 font-display text-sm font-black uppercase tracking-widest text-night">Punaises de lit ? Cafards ?</p>
                        <p className="mt-1 text-xs text-slate-500">Scannez, réservez, on intervient sous 2h</p>
                    </div>
                </Reveal>
            </div>
        </section>

        <CtaSection
            title="Parlons de vos logements"
            subtitle="Premier échange sans engagement : nous étudions votre parc et vous proposons un fonctionnement sur mesure."
        />
    </div>
);

export default Pros;
